import { memo, useCallback, useEffect, useMemo } from 'react'

import BackButton from '@batical/calculPage/src/BackButton'
import { calculBySections } from '@batical/calculPage/src/SectionCalculList'
import { isValidString } from '@batical/common'
import { sectionColors } from '@batical/design'
import { selectSectionCode, setCalcul } from '@batical/reducer/src'
import { CalculCodeStateKeys } from '@batical/types/src'
import { Box, Card, Container, Typography, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { FaChevronRight } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import Page from 'src/components/Page'

const CalculCard = styled(Card)(() => ({
  cursor: 'pointer',
  borderRadius: 12,
  border: '1px solid rgba(0, 0, 0, 0.08)',
  background: 'linear-gradient(135deg, #ffffff 0%, #fafbfc 100%)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    border: '1px solid rgba(0, 0, 0, 0.12)'
  },
  '&:active': {
    transform: 'translateY(-2px) scale(1.01)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
  }
}))

const CalculsList = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const sectionCode = useSelector(selectSectionCode)
  const calculsList = useMemo(
    () => calculBySections[sectionCode]?.calculs ?? [],
    [sectionCode]
  )
  const navigate = useNavigate()
  const tint = sectionColors[sectionCode] ?? '#4277b9'

  const goToCalcul = useCallback(
    (id: string) => {
      dispatch(setCalcul(id as CalculCodeStateKeys))
      navigate(`/dashboard/${id}`)
    },
    [dispatch, navigate]
  )

  const onClickSection = useCallback(
    (id: string) => {
      goToCalcul(id)
    },
    [goToCalcul]
  )

  useEffect(() => {
    if (isValidString(sectionCode)) {
      /* noop */
    }
  }, [sectionCode])

  return (
    <Page title={`BatiKal | ${t(sectionCode || '')}`}>
      <Container disableGutters maxWidth="md" sx={{ p: 1, flexDirection: 'column', alignItems: 'center', pt: 0 }}>
        <Box
          sx={{
            width: '100%',
            mb: 2,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          <BackButton />
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#1a202c',
                mb: 0.5,
                background: `linear-gradient(135deg, ${tint} 0%, ${tint}dd 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
              {t(sectionCode)}
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748b', fontSize: '1rem', fontWeight: 400 }}>
              Sélectionnez un calcul pour commencer
            </Typography>
          </Box>
          <Box sx={{ width: 48 }} />
        </Box>

        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {calculsList.map((item: string, index: number) => {
            const onClickItem = () => onClickSection(item)
            return (
              <CalculCard
                key={item}
                onClick={onClickItem}
                sx={{
                  '&:hover .chevron-icon': {
                    transform: 'translateX(4px)',
                    color: tint
                  }
                }}>
                <Box
                  sx={{
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    minHeight: 60
                  }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${tint} 0%, ${tint}dd 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 2,
                        boxShadow: `0 4px 12px ${tint}30`
                      }}>
                      <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem' }}>{index + 1}</Typography>
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a202c', fontSize: '1rem', lineHeight: 1.4, flex: 1 }}>
                      {t(item)}
                    </Typography>
                  </Box>
                  <FaChevronRight
                    className="chevron-icon"
                    color="#94a3b8"
                    size={18}
                    style={{ transition: 'all 0.3s ease', flexShrink: 0 }}
                  />
                </Box>
              </CalculCard>
            )
          })}
        </Box>
      </Container>
    </Page>
  )
}

export default memo(CalculsList)
